import { cn } from '@/lib/utils';
import {
  CONFIGURATION_AREA_LABELS,
  DEPTH_LABELS,
  SECTION_LABELS,
  STATUS_LABELS,
  THEME_LABELS,
  USAGE_PLACE_LABELS,
  USAGE_RHYTHM_LABELS,
} from '@/lib/company-qualification';
import {
  type CompanyQualificationSheet,
  type QualificationItem,
  type QualificationSectionKey,
  type QualificationStatus,
  type QualificationThemeKey,
  QUALIFICATION_THEMES,
  SYNTHESIS_SECTIONS,
} from '@shared/types/company-qualification';

const DOT: Record<QualificationStatus, string> = {
  validated: 'bg-emerald-600',
  observed: 'bg-sky-600',
  hypothesis: 'bg-amber-500',
  gap: 'bg-stone-400',
};

export const Fact = ({ item }: { item: QualificationItem }) => (
  <li className="flex gap-3">
    <span className={cn('mt-[0.55rem] h-2 w-2 shrink-0 rounded-full', DOT[item.status])} title={STATUS_LABELS[item.status]} />
    <div className="min-w-0">
      <p className={cn('leading-7 text-domino-ink', item.status === 'gap' && 'italic text-domino-inkSoft')}>{item.text}</p>
      <p className="text-xs text-domino-inkSoft">
        {STATUS_LABELS[item.status]}
        {item.source ? (
          <> · {item.source.ref && /^https?:\/\//.test(item.source.ref)
            ? <a href={item.source.ref} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">{item.source.label}</a>
            : item.source.label}</>
        ) : null}
      </p>
    </div>
  </li>
);

const Sections = ({ sheet, sections }: { sheet: CompanyQualificationSheet; sections: readonly QualificationSectionKey[] }) => (
  <>
    {sections.filter((key) => sheet.sections[key].length > 0).map((key) => (
      <div key={key} className="mt-5">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-domino-inkSoft">{SECTION_LABELS[key].label}</h4>
        <ul className="mt-2 space-y-3">{sheet.sections[key].map((item) => <Fact key={item.id} item={item} />)}</ul>
      </div>
    ))}
  </>
);

const Entry = ({ dismissed, children }: { dismissed: boolean; children: React.ReactNode }) => (
  <li className={cn('leading-7', dismissed && 'text-stone-400 line-through')}>{children}</li>
);

/** The whole proposal: configuration, applications, how to use Domino, success and first proof. */
export const ProposalReadView = ({ sheet }: { sheet: CompanyQualificationSheet }) => {
  const { proposal } = sheet;
  return (
        <section>
          <h3 className="border-b border-domino-cardBorder pb-2 font-display text-xl font-semibold text-domino-ink">Proposition</h3>
          {proposal.configuration.length > 0 && (
            <div className="mt-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-domino-inkSoft">Configuration</h4>
              <ol className="mt-2 list-decimal space-y-2 pl-5">
                {proposal.configuration.map((entry) => (
                  <Entry key={entry.id} dismissed={entry.decision === 'dismissed'}>
                    <span className="font-medium">{CONFIGURATION_AREA_LABELS[entry.area]}</span> — {entry.text}
                    {entry.why && <span className="block text-sm text-domino-inkSoft">{entry.why}</span>}
                  </Entry>
                ))}
              </ol>
            </div>
          )}
          {proposal.apps.length > 0 && (
            <div className="mt-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-domino-inkSoft">Applications</h4>
              <div className="mt-2 space-y-3">
                {proposal.apps.map((app) => (
                  <div key={app.id} className={cn('rounded-lg border border-domino-cardBorder px-4 py-3', app.decision === 'dismissed' && 'opacity-50')}>
                    <p className="font-semibold text-domino-ink">{app.title}</p>
                    <dl className="mt-1 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm">
                      <dt className="text-domino-inkSoft">Goulot</dt><dd>{app.bottleneck}</dd>
                      {app.cost && <><dt className="text-domino-inkSoft">Coût</dt><dd>{app.cost}</dd></>}
                      <dt className="text-domino-inkSoft">Mesure</dt><dd>{app.measure}</dd>
                      {app.truthSet && <><dt className="text-domino-inkSoft">Jeu de vérité</dt><dd>{app.truthSet}</dd></>}
                      {app.expected && <><dt className="text-domino-inkSoft">Attendu</dt><dd>{app.expected}</dd></>}
                    </dl>
                  </div>
                ))}
              </div>
            </div>
          )}
          {proposal.usage.length > 0 && (
            <div className="mt-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-domino-inkSoft">Utilisation de Domino</h4>
              <ul className="mt-2 space-y-2">
                {proposal.usage.map((entry) => (
                  <Entry key={entry.id} dismissed={entry.decision === 'dismissed'}>
                    <span className="font-medium">{USAGE_RHYTHM_LABELS[entry.rhythm]}, {USAGE_PLACE_LABELS[entry.place]}</span> — {entry.who} : {entry.what}
                  </Entry>
                ))}
              </ul>
            </div>
          )}
          {[['Réussite', proposal.success], ['Première preuve', proposal.firstProof], ['Ce qui n’est pas proposé', proposal.notProposed]]
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div key={label} className="mt-5">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-domino-inkSoft">{label}</h4>
                <p className="mt-1 leading-7 text-domino-ink">{value}</p>
              </div>
            ))}
        </section>
  );
};

/** The sheet read as a document: themes with their synthesis, facts with a discreet status, then the proposal. */
export const QualificationReadView = ({ sheet, focused, onPage }: {
  sheet: CompanyQualificationSheet;
  focused: QualificationThemeKey | null;
  /** Opens a theme's page of the restitution when it has one. */
  onPage?: (theme: QualificationThemeKey) => void;
}) => {
  const themes = QUALIFICATION_THEMES.filter(({ key, sections }) => (!focused || key === focused)
    && (sheet.themes[key].summary || sections.some((section) => sheet.sections[section].length > 0)));
  const { proposal } = sheet;
  const hasProposal = proposal.configuration.length + proposal.apps.length + proposal.usage.length > 0
    || Boolean(proposal.success || proposal.firstProof || proposal.notProposed);
  if (themes.length === 0 && !hasProposal) {
    return <p className="text-sm text-domino-inkSoft">Rien d’écrit pour l’instant : commencez avec l’analyste, ou passez en mode « Modifier ».</p>;
  }
  return (
    <article className="max-w-3xl space-y-10 rounded-xl border border-domino-cardBorder bg-domino-warm px-8 py-8">
      {themes.map(({ key, sections }) => (
        <section key={key}>
          <div className="flex items-baseline justify-between gap-3 border-b border-domino-cardBorder pb-2">
            <h3 className="font-display text-xl font-semibold text-domino-ink">{THEME_LABELS[key].label}</h3>
            <div className="flex items-baseline gap-3">
              {sheet.themes[key].depth !== 'surface' && <span className="text-xs text-domino-inkSoft">{DEPTH_LABELS[sheet.themes[key].depth]}</span>}
              {onPage && sheet.restitution.pages[key] && (
                <button type="button" onClick={() => onPage(key)} className="text-sm font-medium text-domino-aubergine hover:underline">Voir l’analyse →</button>
              )}
            </div>
          </div>
          {sheet.themes[key].summary && <p className="mt-3 text-[15px] leading-7 text-domino-ink">{sheet.themes[key].summary}</p>}
          <Sections sheet={sheet} sections={sections} />
        </section>
      ))}
      {!focused && SYNTHESIS_SECTIONS.some((key) => sheet.sections[key].length > 0) && (
        <section>
          <h3 className="border-b border-domino-cardBorder pb-2 font-display text-xl font-semibold text-domino-ink">Synthèse</h3>
          <Sections sheet={sheet} sections={SYNTHESIS_SECTIONS} />
        </section>
      )}
      {!focused && hasProposal && <ProposalReadView sheet={sheet} />}
    </article>
  );
};
